import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { marcaAdapter } from "@/data";
import { toast } from "@/hooks/use-toast";
import { MarcaFormDialog } from "./MarcaFormDialog";
import { MarcaDeleteDialog } from "./MarcaDeleteDialog";
import type { Marca } from "@/types/marca";

export function MarcaTable() {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<Marca[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedItem, setSelectedItem] = useState<Marca | null>(null);

  useEffect(() => {
    loadItems();
  }, [search]);

  const loadItems = async () => {
    try {
      setIsLoading(true);
      const response = await marcaAdapter.list({ search });
      setItems(response.items);
    } catch (error) {
      toast({ title: "Erro", description: "Não foi possível carregar as marcas.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setFormMode("create");
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item: Marca) => {
    setFormMode("edit");
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const handleDelete = (item: Marca) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    loadItems();
    toast({ title: "Sucesso", description: `Marca ${formMode === "create" ? "criada" : "atualizada"} com sucesso.` });
  };

  const handleDeleteSuccess = () => {
    setIsDeleteOpen(false);
    loadItems();
    toast({ title: "Sucesso", description: "Marca excluída com sucesso." });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar marca..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Ordem</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id_marca}>
                <TableCell className="font-medium">{item.nome}</TableCell>
                <TableCell className="text-muted-foreground">{item.descricao || "-"}</TableCell>
                <TableCell>{item.ordem}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(item)}
                      disabled={item.sistema}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <MarcaFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        mode={formMode}
        item={selectedItem}
        onSuccess={handleFormSuccess}
      />
      <MarcaDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        item={selectedItem}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}
